/**
 * RBAC Permission Utilities
 *
 * This is the SINGLE source of truth for all permission/role checks
 * in the frontend.  Components should never hard-code role or
 * permission strings — import from here instead.
 */

// ─── Role constants ──────────────────────────────────────────
export const ROLES = Object.freeze({
  ADMIN: 'admin',
  INSURANCE_OFFICER: 'aic_officer',  // legacy User.role value
  OPS_OFFICER: 'agent',              // legacy User.role value
  FARMER: 'farmer',
  CREDIT_OFFICER: 'finance_manager', // legacy User.role value
  VENDOR: 'vendor',
});

// RBAC role names (as stored in the roles table)
export const RBAC_ROLES = Object.freeze({
  ADMIN: 'Admin',
  INSURANCE_OFFICER: 'InsuranceOfficer',
  OPS_OFFICER: 'OpsOfficer',
  FARMER: 'Farmer',
  CREDIT_OFFICER: 'CreditOfficer',
  VENDOR: 'Vendor',
});

// ─── Permission constants (domain-prefixed) ──────────────────
export const PERMISSIONS = Object.freeze({
  // insurance.*
  INSURANCE_VIEW:            'insurance.view',
  INSURANCE_MANAGE:          'insurance.manage',
  INSURANCE_CLAIMS_VIEW:     'insurance.claims.view',
  INSURANCE_CLAIMS_SUBMIT:   'insurance.claims.submit',
  INSURANCE_CLAIMS_PROCESS:  'insurance.claims.process',
  INSURANCE_CLAIMS_APPROVE:  'insurance.claims.approve',

  // credit.*
  CREDIT_VIEW:               'credit.view',
  CREDIT_APPLY:              'credit.apply',
  CREDIT_APPROVE:            'credit.approve',
  CREDIT_MANAGE:             'credit.manage',
  CREDIT_ESCROW_VIEW:        'credit.escrow.view',
  CREDIT_ESCROW_MANAGE:      'credit.escrow.manage',
  CREDIT_ESCROW_PAYOUT:      'credit.escrow.payout',
  CREDIT_SETTLEMENT_VIEW:    'credit.settlement.view',
  CREDIT_SETTLEMENT_MANAGE:  'credit.settlement.manage',

  // system.*
  SYSTEM_USERS_VIEW:         'system.users.view',
  SYSTEM_USERS_MANAGE:       'system.users.manage',
  SYSTEM_ROLES_MANAGE:       'system.roles.manage',
  SYSTEM_ROLES_ASSIGN:       'system.roles.assign',
  SYSTEM_SETTINGS:           'system.settings',
  SYSTEM_ANALYTICS:          'system.analytics',
  SYSTEM_NOTIFICATIONS:      'system.notifications',
  SYSTEM_SMS:                'system.sms',
  SYSTEM_AUDIT:              'system.audit',

  // operations.*
  OPERATIONS_LEADS_VIEW:     'operations.leads.view',
  OPERATIONS_LEADS_MANAGE:   'operations.leads.manage',
  OPERATIONS_FARMS_VIEW:     'operations.farms.view',
  OPERATIONS_FARMS_MANAGE:   'operations.farms.manage',
  OPERATIONS_PONDS_VIEW:     'operations.ponds.view',
  OPERATIONS_PONDS_MANAGE:   'operations.ponds.manage',
  OPERATIONS_KYC_VIEW:       'operations.kyc.view',
  OPERATIONS_KYC_APPROVE:    'operations.kyc.approve',
  OPERATIONS_KYC_REJECT:     'operations.kyc.reject',
  OPERATIONS_DOCUMENTS:      'operations.documents',

  // marketplace.*
  MARKETPLACE_VIEW:          'marketplace.view',
  MARKETPLACE_MANAGE:        'marketplace.manage',
  MARKETPLACE_ORDERS_CREATE: 'marketplace.orders.create',

  // farmer.*
  FARMER_DASHBOARD:          'farmer.dashboard',
  FARMER_KYC_SUBMIT:         'farmer.kyc.submit',
  FARMER_KYC_VIEW:           'farmer.kyc.view',
  FARMER_FARMS_VIEW:         'farmer.farms.view',
  FARMER_FARMS_MANAGE:       'farmer.farms.manage',
  FARMER_PONDS_VIEW:         'farmer.ponds.view',
  FARMER_PONDS_MANAGE:       'farmer.ponds.manage',
  FARMER_POLICIES_VIEW:      'farmer.policies.view',
  FARMER_CLAIMS_SUBMIT:      'farmer.claims.submit',
  FARMER_CLAIMS_VIEW:        'farmer.claims.view',
  FARMER_CREDIT_VIEW:        'farmer.credit.view',
  FARMER_CREDIT_APPLY:       'farmer.credit.apply',
  FARMER_MARKETPLACE_VIEW:   'farmer.marketplace.view',
  FARMER_MARKETPLACE_ORDER:  'farmer.marketplace.order',
});


// ─── Permission check utilities ──────────────────────────────

/**
 * Check if the user has a specific permission.
 *
 * @param {Object}        user       – user object from AuthContext
 * @param {string|string[]} permission – single permission code or array
 * @returns {boolean} true if user has ANY of the listed permissions
 */
export const hasPermission = (user, permission) => {
  if (!user) return false;

  // Admin always has all permissions
  if (user.role === ROLES.ADMIN) return true;

  const userPerms = user.permissions || [];
  if (!userPerms.length) {
    // Fallback: no permissions loaded yet — use legacy role-based check
    return _legacyPermissionCheck(user, permission);
  }

  const required = Array.isArray(permission) ? permission : [permission];
  return required.some((p) => userPerms.includes(p));
};

/**
 * Check if the user has ALL of the listed permissions.
 */
export const hasAllPermissions = (user, permissions) => {
  if (!user) return false;
  if (user.role === ROLES.ADMIN) return true;

  const userPerms = user.permissions || [];
  const required = Array.isArray(permissions) ? permissions : [permissions];
  return required.every((p) => userPerms.includes(p));
};

/**
 * Check whether the user object has one of the allowed legacy roles.
 *
 * @param {Object}          user         – user record from AuthContext
 * @param {string|string[]} allowedRoles – single role or array of roles
 * @returns {boolean}
 */
export const hasRole = (user, allowedRoles) => {
  if (!user?.role) return false;
  const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
  return roles.some((r) => user.role === r);
};

/**
 * Check whether the user has one of the given RBAC role names.
 */
export const hasRbacRole = (user, rbacRoleNames) => {
  if (!user) return false;
  const userRoles = user.rbac_roles || user.roles || [];
  const required = Array.isArray(rbacRoleNames) ? rbacRoleNames : [rbacRoleNames];
  return required.some((r) => userRoles.includes(r));
};

/**
 * Return true when the user is any kind of internal / admin user.
 */
export const isInternalUser = (user) =>
  hasRole(user, [ROLES.ADMIN, ROLES.INSURANCE_OFFICER, ROLES.OPS_OFFICER, ROLES.CREDIT_OFFICER]);

/**
 * Return the default dashboard path for a given role.
 */
export const getDashboardPath = (role) => {
  switch (role) {
    case ROLES.FARMER:
      return '/farmer/dashboard';
    case ROLES.OPS_OFFICER:
      return '/agent/dashboard';
    case ROLES.ADMIN:
    case ROLES.INSURANCE_OFFICER:
      return '/admin/dashboard';
    case ROLES.CREDIT_OFFICER:
      return '/admin/dashboard';
    case ROLES.VENDOR:
      return '/vendor/dashboard';
    default:
      return '/login';
  }
};

/**
 * Check if user can access a specific route based on roles
 */
export const canAccessRoute = (user, routeRoles) => {
  if (!routeRoles || routeRoles.length === 0) return true;
  if (!user?.role) return false;
  return routeRoles.includes(user.role);
};


// ─── Internal: legacy role-based fallback ────────────────────

/**
 * When the permissions array is not yet populated (e.g., old login
 * session in localStorage), fall back to role-based checks.
 */
function _legacyPermissionCheck(user, permission) {
  if (!user?.role) return false;

  const required = Array.isArray(permission) ? permission : [permission];

  // Map legacy roles to domain modules they can access
  const legacyRoleModules = {
    [ROLES.ADMIN]:             ['insurance', 'credit', 'system', 'operations', 'marketplace', 'farmer'],
    [ROLES.INSURANCE_OFFICER]: ['insurance', 'operations', 'system'],
    [ROLES.OPS_OFFICER]:       ['operations', 'marketplace', 'insurance', 'credit', 'system'],
    [ROLES.CREDIT_OFFICER]:    ['credit', 'insurance', 'operations', 'system'],
    [ROLES.FARMER]:            ['farmer', 'marketplace'],
    [ROLES.VENDOR]:            ['marketplace', 'farmer'],
  };

  const allowedModules = legacyRoleModules[user.role] || [];

  return required.some((p) => {
    const module = p.split('.')[0]; // e.g. "insurance" from "insurance.view"
    return allowedModules.includes(module);
  });
}
